import { apiClient } from '@api/apiClient';
import { V4ApiResponse } from '@api/types';
import { closeModal } from '@store/modals';
import { ICustomerPayment, orderStore, setOrderStore } from '@store/orders';
import { TabType } from '@store/orders/types';
import { createSignal, onMount, Show } from 'solid-js';
import { formatAmount, handleToast } from '@utils/utils';
import { createError } from '@utils/errorUtils';
import { ToastType } from '@components/Toast/Toast';
import { OperationTypeV4 } from '@typeDefinitions/operationTypes';
import { PaymentViewModel } from '@typeDefinitions/paymentTypes';
import { CircularProgress, Skeleton } from '@atgfr8/atg-ui-tailwind';
import { buildChangeTrackingRequestConfig } from '@changeTracking/utils/buildChangeTrackingHeaders';
import { CHANGE_TRACKING_SOURCES } from '@changeTracking/utils/changeTrackingRegistry';

import { checkDetailsModalId } from './CarrierPayments';

type checkDetailsType = {
  code: string | null;
  txnId: string | null;
  activeDate: string | null;
  amount: number;
  amountUsed: number;
  amountRemaining: number;
  createdDate: string | null;
  issuedTo: string | null;
  paidTo: string | null;
  notes: string | null;
  uses: number;
  status: string | null;
  voidDate: string | null;
  voided: boolean;
};

export const CheckDetails = (props: {
  activeRow: ICustomerPayment | PaymentViewModel;
  isLoad?: boolean;
}) => {
  const [checkDetails, setCheckDetails] = createSignal<checkDetailsType | null>(
    null,
  );

  const load = () => {
    if (orderStore.activeTab.type === TabType.load) {
      return orderStore.order.loads[orderStore.activeTab.index];
    }
    return null;
  };

  const [loading, setLoading] = createSignal(true);
  const [voidProcessing, setVoidProcessing] = createSignal(false);
  onMount(async () => {
    setLoading(true);
    try {
      const moneyCode = String(props.activeRow.referenceNum);

      const v4Response: V4ApiResponse<checkDetailsType> = await apiClient.get(
        '/Load/CheckDetails',
        {
          params: {
            code: moneyCode,
          },
        },
      );
      if (!v4Response.isSuccess) {
        handleToast(
          ToastType.Error,
          v4Response.errors[0]?.message ?? 'Failed to check load details',
        );
        return;
      }
      setCheckDetails(v4Response.value);
    } catch (error) {
      const err = createError(error, 'Failed to check load details');
      handleToast(ToastType.Error, err.message);
    } finally {
      setLoading(false);
    }
  });

  const voidCheck = async () => {
    let response: V4ApiResponse<unknown>;
    try {
      const isLoadVoid = load() || Boolean(props.isLoad);
      response = await apiClient.post(
        isLoadVoid
          ? `/Load/LineItem/${props.activeRow.id}/VoidCheck`
          : `/VendorContract/LineItem/${props.activeRow.id}/VoidCheck`,
        { paymentId: props.activeRow.id },
        buildChangeTrackingRequestConfig([
          isLoadVoid
            ? CHANGE_TRACKING_SOURCES.orderDetails.carrierBillingVoidCheck
            : CHANGE_TRACKING_SOURCES.orderDetails.vendorPaymentsVoidCheck,
        ]),
      );
    } catch (error) {
      const err = createError(error, 'Failed to void check');
      handleToast(ToastType.Error, err.message);
      return;
    }
    if (response.isSuccess) {
      if (load()?.status === 'Billed' || load()?.status === 'Paid') {
        handleToast(
          ToastType.Caution,
          'Successfully voided check. You must save the order to update invoicing',
        );
      } else {
        handleToast(ToastType.Success, 'Successfully voided check');
      }
      if (load()) {
        setOrderStore(
          'order',
          'loads',
          orderStore.activeTab.index,
          'payments',
          (payments) =>
            payments?.map((payment) => {
              if (payment.referenceNum === props.activeRow.referenceNum) {
                return {
                  ...payment,
                  amount: 0,
                  operationType: 'Update' as OperationTypeV4,
                };
              }
              return payment;
            }),
        );
        setOrderStore(
          'order',
          'loads',
          orderStore.activeTab.index,
          'lineItems',
          (lineItems) =>
            lineItems?.map((lineItem) => {
              if (lineItem.referenceNumber === props.activeRow.referenceNum) {
                return {
                  ...lineItem,
                  status: 'Unsettled',
                  readOnly: false,
                  referenceNumber: undefined,
                  operationType: 'Update' as OperationTypeV4,
                };
              }
              return lineItem;
            }),
        );
      }
      if (orderStore.activeTab.type === TabType.vendor) {
        setOrderStore(
          'order',
          'vendors',
          orderStore.activeTab.index,
          'payments',
          (payments) =>
            payments?.map((payment) => {
              if (payment.referenceNum === props.activeRow.referenceNum) {
                return {
                  ...payment,
                  operationType: 'Delete' as OperationTypeV4,
                };
              }
              return payment;
            }),
        );
        setOrderStore(
          'order',
          'vendors',
          orderStore.activeTab.index,
          'lineItems',
          (lineItems) =>
            lineItems?.map((lineItem) => {
              if (lineItem.referenceNumber === props.activeRow.referenceNum) {
                return {
                  ...lineItem,
                  status: 'Unsettled',
                  readOnly: false,
                  operationType: 'Update' as OperationTypeV4,
                };
              }
              return lineItem;
            }),
        );
      }
    } else {
      handleToast(
        ToastType.Error,
        response.errors[0]?.message ?? 'Failed to void check',
      );
    }
    closeModal(checkDetailsModalId);
  };

  return (
    <>
      {loading() ? (
        <div>
          <div class="font-bold text-md py-2">EFS Check Details</div>
          <div>
            Please wait while the system retrieves the EFS Check Information
          </div>
          <div>
            <div class="flex flex-col gap-y-2">
              <div class="flex flex-row flex-1 align-items-bottom gap-x-8">
                <div class="self-center">Code:</div>
                <div class="self-end">
                  <Skeleton
                    variant="rectangular"
                    width="300px"
                    height={40}
                  ></Skeleton>
                </div>
              </div>
              <div class="flex flex-row flex-1 align-items-bottom gap-x-2">
                <div class="self-center">Issued To:</div>
                <div class="self-end">
                  <Skeleton
                    variant="rectangular"
                    width="300px"
                    height={40}
                  ></Skeleton>
                </div>
              </div>
              <div class="flex flex-row flex-1 align-items-bottom gap-x-6">
                <div class="self-center">Paid To:</div>
                <div class="self-end">
                  <Skeleton
                    variant="rectangular"
                    width="300px"
                    height={40}
                  ></Skeleton>
                </div>
              </div>
            </div>

            <div class="flex justify-between items-center mt-4">
              <div>
                <div class="font-semibold">Amount</div>
                <div class="text-green-600 font-bold">
                  <Skeleton
                    variant="rectangular"
                    width="100px"
                    height={40}
                  ></Skeleton>
                </div>
              </div>
              <div>
                <div class="font-semibold">Used</div>
                <div class="text-green-600 font-bold">
                  <Skeleton
                    variant="rectangular"
                    width="100px"
                    height={40}
                  ></Skeleton>
                </div>
              </div>
              <div>
                <div class="font-semibold">Available</div>
                <div class="text-green-600 font-bold">
                  <Skeleton
                    variant="rectangular"
                    width="100px"
                    height={40}
                  ></Skeleton>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div class="font-bold text-md py-2">EFS Check Details</div>
          <div>
            <div class="flex flex-col gap-y-2">
              <div>Code: {checkDetails()?.code}</div>
              <div>Issued To: {checkDetails()?.issuedTo}</div>
              <div>Paid To: {checkDetails()?.paidTo}</div>
            </div>

            <div class="flex justify-between items-center mt-4">
              <div>
                <div class="font-semibold">Amount</div>
                <div class="text-green-600 font-bold">
                  {formatAmount(checkDetails()?.amount)}
                </div>
              </div>
              <div>
                <div class="font-semibold">Used</div>
                <div class="text-green-600 font-bold">
                  {formatAmount(checkDetails()?.amountUsed)}
                </div>
              </div>
              <div>
                <div class="font-semibold">Available</div>
                <div class="text-green-600 font-bold">
                  {formatAmount(checkDetails()?.amountRemaining)}
                </div>
              </div>
              <Show when={checkDetails()?.amountUsed === 0}>
                <div class="flex items-center gap-1">
                  <Show when={voidProcessing()}>
                    <CircularProgress size={20} />
                  </Show>
                  <button
                    class={
                      'bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-4 rounded-sm disabled:pointer-events-none disabled:opacity-50'
                    }
                    onClick={async () => {
                      setVoidProcessing(true);
                      await voidCheck();
                      setVoidProcessing(false);
                    }}
                    disabled={voidProcessing()}
                  >
                    Void
                  </button>
                </div>
              </Show>
            </div>
          </div>
        </>
      )}
    </>
  );
};
